import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { prepareUIFilters } from '../../model/prepareUIFilters';
import type { FilterValue, Filters } from '../../model/types';
import { useFiltersParamsContext } from '../FiltersParamsContext/FiltersParamsContext';

interface ContextState {
  filters: Filters;
  filtersMap: Record<string, Filters[number]['value']>;
  updateFilters: (id: string, value?: FilterValue) => void;
  submitFilters: () => void;
  resetToInitialFilters: () => void;
}

const Context = createContext<ContextState | null>(null);

interface Props {
  activeFilters: Filters;
  children: ReactNode;
  onChange: (v: Filters) => void;
}

export const FiltersProvider = ({
  activeFilters,
  children,
  onChange,
}: Props) => {
  const { configsMap, paramsData, resetParamsData } = useFiltersParamsContext();
  const [filters, setFilters] = useState(
    activeFilters.map((filter, index) => {
      return {
        filter,
        order: index,
      };
    }),
  );
  const filtersMap = useMemo(() => {
    return Object.fromEntries(
      filters.map((filter) => {
        return [filter.filter.id, filter.filter.value];
      }),
    );
  }, [filters]);

  useEffect(() => {
    setFilters(
      activeFilters.map((filter, index) => {
        return {
          filter,
          order: index,
        };
      }),
    );
  }, [activeFilters]);

  return (
    <Context.Provider
      value={{
        filters: filters.map((f) => {
          return f.filter;
        }),
        filtersMap,
        updateFilters: (id, value) => {
          const minPosition =
            Math.min(
              ...filters.map((filter) => {
                return filter.order;
              }),
            ) - 1;

          setFilters((oldFilters) => {
            const newFilters: typeof oldFilters = [];
            let filterAdded = false;

            oldFilters.forEach((f) => {
              if (f.filter.id === id) {
                filterAdded = true;

                if (value === undefined) {
                  return;
                }

                newFilters.push({
                  filter: { id, value },
                  order: minPosition,
                });
              } else {
                newFilters.push(f);
              }
            });

            if (!filterAdded && value) {
              newFilters.push({
                filter: { id, value },
                order: minPosition,
              });
            }

            return newFilters;
          });
        },
        submitFilters: () => {
          const newFilters = prepareUIFilters({
            filters: filters
              .toSorted((a, b) => {
                return a.order - b.order;
              })
              .map((f) => {
                return f.filter;
              }),
            paramsData,
            configsMap,
          });

          resetParamsData(newFilters);
          onChange(newFilters);
        },
        resetToInitialFilters: () => {
          setFilters(
            activeFilters.map((filter, index) => {
              return {
                filter,
                order: index,
              };
            }),
          );
        },
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useFiltersContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useFiltersContext hook used outside <FiltersProvider />');
  }

  return context;
};
